import Image from 'next/image'
import {FC} from 'react'

import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {getContactContent} from '@/content/contact'
import {TeamMemberType} from '@/content/team'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE, USER_TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

type ProfileHeroProps = {
  teamMember: TeamMemberType
}

export const ProfileHero: FC<ProfileHeroProps> = ({teamMember}) => {
  const {t} = useTranslation()
  const {linkedIn} = getContactContent().socials
  const {name, role, alternativeRole, linkedInProfileLink, photoSrc, photoAlt} = teamMember

  return (
    <>
      <div className="hero">
        <div className="content">
          <Text variant={TextVariant.h1}>{t(name)}</Text>
          <div className="role">
            <Text
              variant={TextVariant.text24}
              fontWeight={TextFontWeight.light}
              color={theme.palette.text.light}
            >
              {t(alternativeRole ?? role)}
            </Text>
          </div>
          <a
            href={linkedInProfileLink}
            target="_blank"
            rel="noopener noreferrer"
            className="linkedin"
          >
            <Image src={linkedIn.imageSrc} alt={linkedIn.imageAlt} width={32} height={32} />
          </a>
        </div>
        <div className="photo">
          <Image
            src={photoSrc}
            alt={photoAlt}
            style={{objectFit: 'cover'}}
            fill
            priority
            sizes={`${MOBILE_SCREENSIZE} 100vw, ${USER_TABLET_SCREENSIZE} 250px, 350px`}
          />
        </div>
      </div>

      <style jsx>{`
        .hero {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          padding: ${theme.spacing[5.5]} ${theme.spacing[6]};
          background-color: ${theme.palette.background.butter};
          border-radius: 12px;
        }
        .content {
          white-space: nowrap;
        }
        .role {
          margin-top: ${theme.spacing[3.5]};
        }
        .linkedin {
          display: inline-flex;
          margin-top: ${theme.spacing[3]};
          overflow: hidden;
          border-radius: 4px;
        }
        .photo {
          position: relative;
          width: 100%;
          max-width: 350px;
          height: 350px;
          overflow: hidden;
          border-top-right-radius: 12px;
          border-bottom-right-radius: 12px;
          margin: -${theme.spacing[5.5]} -${theme.spacing[6]} -${theme.spacing[5.5]} ${theme.spacing[6]};
        }
        @media ${USER_TABLET_SCREENSIZE} {
          .hero {
            padding: ${theme.spacing[5]};
          }
          .photo {
            max-width: 250px;
            height: 250px;
            margin: -${theme.spacing[5]} -${theme.spacing[5]} -${theme.spacing[5]} ${theme.spacing[5.5]};
          }
        }
        @media ${MOBILE_SCREENSIZE} {
          .hero {
            flex-direction: column-reverse;
            align-items: flex-start;
            padding: ${theme.spacing[4]};
          }
          .content {
            white-space: inherit;
          }
          .photo {
            width: calc(100% + ${theme.spacing[4]} * 2);
            max-width: inherit;
            height: 300px;
            border-top-left-radius: 12px;
            border-top-right-radius: 12px;
            border-bottom-right-radius: 0;
            margin: -${theme.spacing[4]} -${theme.spacing[4]} ${theme.spacing[4]};
          }
        }
      `}</style>
    </>
  )
}
